import { BaseComponent, define } from 'core';
import { OverlayElement } from 'component-overlay';
import { quickAdd } from 'component-cart-upsell';

/**
 * Hızlı bakış penceresi.
 *
 * Varyantlı bir çapraz satış ürününde "Choose options" kullanıcıyı ürün sayfasına
 * gönderiyordu; mobilde bu, sepete eklemek için sayfayı terk edip geri dönmek
 * demek. Burada seçim yapılıp sepete eklenir, sayfa hiç değişmez.
 *
 * Progressive enhancement: tetikleyici GERÇEK bir <a href="/products/...">.
 * JS yoksa (veya henüz yüklenmediyse) link normal çalışır ve tarama motorları
 * onu görür; JS varsa tıklama yakalanıp pencere açılır.
 */
export class QuickView extends OverlayElement {
  connectedCallback() {
    super.connectedCallback();
    this._slot = this.querySelector('[data-quick-view-slot]');
    this._skeleton = this._slot ? this._slot.innerHTML : '';

    // Tek bir belge düzeyi dinleyici: tetikleyiciler ajax ile gelen kartların
    // içinde de olabiliyor (öneri şeritleri, sepet çekmecesi), tek tek bağlamak
    // o kartlarda çalışmazdı.
    this._onDocClick = this._onDocClick.bind(this);
    document.addEventListener('click', this._onDocClick);

    this.addEventListener('variant-picker:change', (event) => this._syncVariant(event.detail.variant));
    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-qv-add]');
      if (button) this._add(button);
    });
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._onDocClick);
    super.disconnectedCallback();
  }

  _onDocClick(event) {
    const trigger = event.target.closest('[data-quick-view]');
    if (!trigger) return;
    // Yeni sekmede açma niyetine karışma.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
    const url = trigger.getAttribute('href') || trigger.dataset.quickView;
    if (!url) return;
    event.preventDefault();
    this._opener = trigger;
    this.load(url);
  }

  async load(productUrl) {
    if (this._slot) this._slot.innerHTML = this._skeleton;
    this.open();
    try {
      const target = new URL(productUrl, window.location.origin);
      target.searchParams.set('view', 'quickview');
      const response = await fetch(target.toString(), { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(String(response.status));
      const html = await response.text();
      if (!this.isOpen) return;
      this._slot.innerHTML = html;
      // Yeni gelen varyant seçicinin ilk hâline göre fiyat zaten sunucudan
      // doğru geliyor; burada sadece odak veriliyor.
      const focusable = this._slot.querySelector('[data-option-input], [data-qv-add]');
      if (focusable) focusable.focus({ preventScroll: true });
    } catch (_error) {
      // Pencere kurtarılamıyorsa kullanıcıyı boş bir kutuda bırakma: linkin
      // kendi hedefine gönder (JS'siz davranışın aynısı).
      this.close();
      window.location.href = productUrl;
    }
  }

  /** Varyant değişince fiyatı sunucudan tazele — istemcide fiyat hesabı YOK. */
  async _syncVariant(variant) {
    if (!variant) return;
    const priceEl = this._slot.querySelector('[data-qv-price]');
    const body = this._slot.querySelector('[data-quick-view-body]');
    if (!priceEl || !body || !this._opener) return;
    const base = this._opener.getAttribute('href') || this._opener.dataset.quickView;
    try {
      const target = new URL(base, window.location.origin);
      target.searchParams.set('view', 'quickview');
      target.searchParams.set('variant', variant.id);
      const response = await fetch(target.toString(), { headers: { Accept: 'text/html' } });
      if (!response.ok) return;
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const fresh = doc.querySelector('[data-qv-price]');
      if (fresh) priceEl.innerHTML = fresh.innerHTML;
    } catch (_error) {

    }
  }

  async _add(button) {
    const form = this._slot.querySelector('[data-qv-form]');
    if (!form) return;
    const id = form.querySelector('[name="id"]');
    const qty = form.querySelector('[data-qv-qty]');
    if (!id || !id.value) return;
    button.dataset.variantId = id.value;
    button.dataset.qty = qty && qty.value ? qty.value : '1';
    await quickAdd(button);
    // quickAdd() artık pencere dışından eklemede sepet çekmecesini açıyor;
    // iki katman üst üste binmesin diye bu pencere kapanıyor.
    setTimeout(() => this.close(), 600);
  }
}

if (!customElements.get('quick-view')) define('quick-view', QuickView);
